
import { AlaskaCartLineModel, AlaskaCartModel } from "@aihCustomBlueprint/Services/AlaskaApiModels";
import { API_URL_CURRENT_FRAGMENT, get, post, ServiceResult } from "@insite/client-framework/Services/ApiService";
import { Cart, getCart, GetCartApiParameter } from "@insite/client-framework/Services/CartService";
import { GetProductCollectionApiV2Parameter } from "@insite/client-framework/Services/ProductServiceV2";
import { CartLineCollectionModel, CartModel, ProductCollectionModel, ProductInventoryDto, RealTimeInventoryModel } from "@insite/client-framework/Types/ApiModels";

const alaskaServiceUrl = "api/v1/CustomCartLineCollection";
const namedCartServiceUri = "api/v1/namedcarts";

export async function removeSelectedCartLines(cartModel: CartModel): Promise<ServiceResult<string>> {
    const result = await post<CartModel, string>(
        `${alaskaServiceUrl}/RemoveSelectedCartLines/${cartModel.id}`, cartModel);

    return {
        result,
        successful: true
    }
}

export function saveCartLinesForPickup(cartLineCollection: CartLineCollectionModel): Promise<CartLineCollectionModel> {
    return post<CartLineCollectionModel, CartLineCollectionModel>(
        `${namedCartServiceUri}/addToPickupCart`, cartLineCollection
    )
}

export function saveCartLinesForDelivery(cartLineCollection: CartLineCollectionModel): Promise<CartLineCollectionModel> {
    const cart = getCart({ cartId: API_URL_CURRENT_FRAGMENT } as GetCartApiParameter); 
    return post<CartLineCollectionModel, CartLineCollectionModel>(
        `${namedCartServiceUri}/addToShippingCart`, cartLineCollection
    )
}

export async function getCartWithLinesForReview(cart: Cart | undefined): Promise<ServiceResult<AlaskaCartModel>> {
    try {

        const ids:string[] = [];
        let alaskaCart = cart as AlaskaCartModel;
        const cartLines = cart?.cartLines;
        if(cartLines){
            cartLines.forEach(cartLine => {
                if(cartLine.productId!=null){
                    if (!ids.includes(cartLine.productId)) {
                        ids.push(cartLine.productId);
                    }
                }
            })
        }
        const productsUrl = "/api/v1/products";
        const param: GetProductCollectionApiV2Parameter = {
            productIds: ids
        }
        const products = await get<ProductCollectionModel>(productsUrl, param);

        const realtimeInventoryUrl = "/api/v1/realtimeinventory";
        const realtimeInventory = await post<GetProductCollectionApiV2Parameter, RealTimeInventoryModel>(realtimeInventoryUrl, param);
        alaskaCart = {
            ...alaskaCart,
            cartLines: getRealTimeInventoryCompleted(realtimeInventory, alaskaCart)
        };

        const data = filterValidCartLines(alaskaCart, products); 
        return {
            successful: true,
            result: data,
        };
    } catch (error) {
        if ("status" in error && (error.status === 400 || error.status === 403 || error.status === 404)) {
            return {
                successful: false,
                errorMessage: error?.errorJson?.message || error?.errorMessage || "error",
                statusCode: error.status,
            };
        }
        throw error;
    }
}

const getRealTimeInventoryCompleted = (realtimeInventory: RealTimeInventoryModel, cart: AlaskaCartModel) => {
    const cartLines: AlaskaCartLineModel[] = [];
    cart?.cartLines?.forEach((cartLine: AlaskaCartLineModel) => {
        cartLine = {
            ...cartLine
        };
        const productInventory = realtimeInventory.realTimeInventoryResults?.find((productInventory: ProductInventoryDto) => productInventory.productId === cartLine.productId);
        if (productInventory) {
            cartLine.qtyOnHand = productInventory.qtyOnHand;
            const inventoryAvailability = productInventory.inventoryAvailabilityDtos?.find(o => o.unitOfMeasure === cartLine.unitOfMeasure);
            const warehouseAvailability = productInventory.inventoryWarehousesDtos?.find(o => o.unitOfMeasure === cartLine.unitOfMeasure);
            if (inventoryAvailability && warehouseAvailability) {
                cartLine.availability = inventoryAvailability.availability;
                if (!cart.hasInsufficientInventory && !cartLine.canBackOrder && !cartLine.quoteRequired && (inventoryAvailability.availability as any).messageType === 2) {
                    cart.hasInsufficientInventory = true;
                }
                cartLine.warehouseAvailability = warehouseAvailability;
                if (productInventory.additionalResults && productInventory.additionalResults["isNoPull"] && productInventory.additionalResults["isNoPull"] === "true") {
                    cartLine.isNoPull = true;
                    cartLine.notes = "";
                }
                if (productInventory.additionalResults && productInventory.additionalResults["zeroAtStockingWarehouse"]) {
                    const number = Number(productInventory.additionalResults["zeroAtStockingWarehouse"]);
                    if (cartLine.qtyOrdered && cartLine.qtyOrdered > number) {
                        cartLine.isNoPull = true;
                        cartLine.notes = "";
                    }
                }
            } else {
                cartLine.availability = { messageType: 0, message: "", requiresRealTimeInventory: false };
            }
        } else {
            cartLine.availability = { messageType: 0 , message: "", requiresRealTimeInventory: false };
        }

        cartLines.push(cartLine);
    });
    return cartLines;
}

const filterValidCartLines = (cart: AlaskaCartModel, products: any) => {
    const linesToReturn = [];
    if(cart?.cartLines){
        // eslint-disable-next-line no-unsafe-optional-chaining
        for (let cartLine of cart?.cartLines) {
            const product = products?.products?.find(function (product: { id: string | null; }) {
                return product.id === cartLine.productId;
            });                
            if (product) {
                let code;
                let shipCode;

                if(product.productCode){
                    code = getHazardCode(product.productCode);
                    shipCode = getShippingCode(product.productCode);
                }

                let added = false;
                if (cartLine.isNoPull) {
                    linesToReturn.push(cartLine);
                    added = true;
                }
                if (code != null && !(code.trim() === '')) {
                    if (cart.fulfillmentMethod !== "PickUp") {
                        cartLine = {
                            ...cartLine,
                            hazardCode : code
                        };
                        if (!added) {
                            linesToReturn.push(cartLine);
                            added = true;
                        }
                    }
                }
                else if (shipCode != null && !(shipCode.trim() === '')) {
                    if (cart.fulfillmentMethod !== "PickUp") {
                        cartLine = {
                            ...cartLine,
                            shipRestrictionCode : shipCode
                        };
                        if (!added) {
                            linesToReturn.push(cartLine);
                            added = true;
                        }
                    }
                }
            } 
        }
    }

    cart = {
        ...cart,
        cartLines: linesToReturn
    };
    return cart;
}

const getHazardCode = (codes: string) => {
    const codeList = codes.split(',');
    for (const code of codeList) {
        if (code === "1" || code === "2" || code === "3" || code === "4") {
            return code;
        }
    }
    return "";
}

const getShippingCode = (codes: string) => {
    const codeList = codes.split(',');
    for (const code of codeList) {
        if (code === "99" || code === "9") {
            return code;
        }
    }
    return "";
}

